class MixMatchDiscountPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('.mix-match-popup__dialog');
    this.status = this.querySelector('[data-mix-match-status], .mix-match-popup__status');
    this.closeButtons = this.querySelectorAll('[data-mix-match-close]');
    this.addButtons = this.querySelectorAll('[data-mix-match-add]');
    this.variantSelectors = this.querySelectorAll('[data-mix-match-variant]');

    this.firstQuantity = Number.parseInt(this.dataset.firstQuantity, 10) || 2;
    this.firstDiscount = Number.parseInt(this.dataset.firstDiscount, 10) || 15;
    this.secondQuantity = Math.max(
      Number.parseInt(this.dataset.secondQuantity, 10) || 3,
      this.firstQuantity + 1
    );
    this.secondDiscount = Number.parseInt(this.dataset.secondDiscount, 10) || 20;
    this.stopAtFinalTier = this.dataset.stopAtFinalTier === 'true';

    this.pendingEligibleAdd = false;
    this.openAfterCartUpdate = false;
    this.currentEligibleQuantity = this.getEligibleCartQuantity();
    this.previousFocus = null;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this);
    this.handleProductAdded = this.handleProductAdded.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    document.addEventListener('click', this.handleDocumentClick, true);
    document.addEventListener('submit', this.handleDocumentSubmit, true);
    window.addEventListener('theme:cart:productAdded', this.handleProductAdded);
    window.addEventListener('theme:cart:updated', this.handleCartUpdated);

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.addButtons.forEach((button) => {
      button.addEventListener('click', () => this.addProduct(button));
    });

    this.variantSelectors.forEach((selector) => {
      selector.addEventListener('change', () => this.updateSelectedVariant(selector));
    });

    this.updateStatus();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick, true);
    document.removeEventListener('submit', this.handleDocumentSubmit, true);
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('theme:cart:productAdded', this.handleProductAdded);
    window.removeEventListener('theme:cart:updated', this.handleCartUpdated);
    document.body.classList.remove('mix-match-popup-open');
  }

  handleDocumentClick(event) {
    const eligibleContainer = event.target.closest('[data-mix-match-eligible="true"]');
    if (!eligibleContainer || this.contains(event.target)) return;

    const addControl = event.target.closest(
      '.product-buy-buttons--primary, .product-quick-add--button'
    );

    if (!addControl) return;

    if (
      addControl.classList.contains('product-quick-add--button') &&
      eligibleContainer.dataset.form !== 'false'
    ) {
      return;
    }

    const variantId =
      eligibleContainer.querySelector('input[name="id"]')?.value ||
      eligibleContainer.dataset.id;
    const quantity =
      eligibleContainer.closest('form')?.querySelector('input[name="quantity"]')?.value || 1;

    this.markEligibleAddPending(variantId, quantity);
  }

  handleDocumentSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
    if (form.querySelector('[data-mix-match-eligible="true"]')) {
      this.markEligibleAddPending(
        form.querySelector('input[name="id"]')?.value,
        form.querySelector('input[name="quantity"]')?.value || 1
      );
    }
  }

  markEligibleAddPending(variantId, quantity = 1) {
    this.pendingEligibleAdd = true;
    this.pendingVariantId = Number.parseInt(variantId, 10) || null;
    this.pendingQuantity = Number.parseInt(quantity, 10) || 1;
    window.clearTimeout(this.pendingEligibleTimer);
    this.pendingEligibleTimer = window.setTimeout(() => {
      this.pendingEligibleAdd = false;
    }, 8000);
  }

  handleProductAdded(event) {
    if (!this.pendingEligibleAdd) return;
    if (this.pendingVariantId && Number.parseInt(event.detail, 10) !== this.pendingVariantId) return;

    this.pendingEligibleAdd = false;
    window.clearTimeout(this.pendingEligibleTimer);
    this.openAfterCartUpdate = true;

    window.clearTimeout(this.cartUpdateFallback);
    this.cartUpdateFallback = window.setTimeout(() => {
      if (!this.openAfterCartUpdate) return;
      this.openAfterCartUpdate = false;
      this.currentEligibleQuantity += this.pendingQuantity;
      this.openIfUseful();
    }, 1200);
  }

  handleCartUpdated() {
    window.clearTimeout(this.cartUpdateFallback);
    this.currentEligibleQuantity = this.getEligibleCartQuantity();
    this.updateStatus();

    if (this.openAfterCartUpdate) {
      this.openAfterCartUpdate = false;
      this.openIfUseful();
    }
  }

  getEligibleCartQuantity() {
    const cart =
      document.querySelector('.cart--root[data-view="drawer"]') ||
      document.querySelector('.cart--root[data-view="mobile"]') ||
      document.querySelector('.cart--root[data-view="desktop"]');

    if (!cart) return this.currentEligibleQuantity || 0;

    return [...cart.querySelectorAll('.cart--item[data-mix-match-eligible="true"]')].reduce(
      (total, item) => {
        const input = item.querySelector('.quantity-selector--input');
        const quantity = Number.parseInt(input?.value, 10) || 0;
        return total + quantity;
      },
      0
    );
  }

  openIfUseful() {
    if (this.stopAtFinalTier && this.currentEligibleQuantity >= this.secondQuantity) return;
    this.open();
  }

  open() {
    if (!this.hidden) return;

    this.previousFocus = document.activeElement;
    this.hidden = false;
    document.body.classList.add('mix-match-popup-open');
    document.addEventListener('keydown', this.handleKeydown);
    this.updateStatus();

    window.requestAnimationFrame(() => this.dialog.focus());
  }

  close() {
    if (this.hidden) return;

    this.hidden = true;
    document.body.classList.remove('mix-match-popup-open');
    document.removeEventListener('keydown', this.handleKeydown);

    if (this.previousFocus?.isConnected) this.previousFocus.focus();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [...this.querySelectorAll(
      'a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.offsetParent !== null);

    if (!focusable.length) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  updateStatus() {
    if (!this.status) return;

    const quantity = this.currentEligibleQuantity || 0;

    if (quantity < this.firstQuantity) {
      const remaining = this.firstQuantity - quantity;
      this.status.textContent = `Add ${remaining} more eligible ${this.pluraliseItem(remaining)} to save ${this.firstDiscount}%.`;
      return;
    }

    if (quantity < this.secondQuantity) {
      const remaining = this.secondQuantity - quantity;
      this.status.textContent = `You've unlocked ${this.firstDiscount}% off. Add ${remaining} more ${this.pluraliseItem(remaining)} to save ${this.secondDiscount}%.`;
      return;
    }

    this.status.textContent = `You've unlocked ${this.secondDiscount}% off eligible products.`;
  }

  pluraliseItem(quantity) {
    return quantity === 1 ? 'item' : 'items';
  }

  updateSelectedVariant(selector) {
    const product = selector.closest('[data-mix-match-product]');
    const option = selector.selectedOptions[0];
    const button = product.querySelector('[data-mix-match-add]');
    const price = product.querySelector('[data-mix-match-price]');
    const image = product.querySelector('.mix-match-popup__image:not(.mix-match-popup__image--placeholder)');

    button.dataset.variantId = selector.value;
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }
  }

  async addProduct(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const product = button.closest('[data-mix-match-product]');
    const error = product.querySelector('[data-mix-match-error]');
    const buttonText = button.querySelector('[data-mix-match-button-text]');
    const variantId = Number.parseInt(button.dataset.variantId, 10);

    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    error.hidden = true;

    try {
      const sectionNames = this.getCartSectionNames();
      const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionNames,
          sections_url: window.location.pathname
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This item could not be added.');

      const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, {
        headers: { Accept: 'application/json' }
      });
      const cart = await cartResponse.json();

      this.updateCartCount(cart.item_count);
      this.currentEligibleQuantity += 1;
      this.updateStatus();
      this.updateCartSections(result.sections || {});

      buttonText.textContent = 'Added';
      window.setTimeout(() => {
        buttonText.textContent = 'Add to cart';
      }, 1600);
    } catch (requestError) {
      error.textContent = requestError.message || 'This item could not be added. Please try again.';
      error.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  getCartSectionNames() {
    const names = ['cart-drawer'];
    const cartPath = this.dataset.cartUrl.replace(/\/$/, '');
    const currentPath = window.location.pathname.replace(/\/$/, '');

    if (currentPath === cartPath) names.push('cart-desktop', 'cart-mobile');
    return names;
  }

  updateCartCount(itemCount) {
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.setAttribute('data-cart-count', itemCount);
    });

    document.querySelector('.layout--viewport')?.setAttribute('data-cart-empty', itemCount === 0);
  }

  updateCartSections(sections) {
    Object.entries(sections).forEach(([sectionName, markup]) => {
      if (!markup) return;

      const view = sectionName.replace(/^cart-/, '');
      window.dispatchEvent(
        new CustomEvent(`theme:cart:${view}:updateHtml`, {
          detail: { section: markup, load_transitions: true }
        })
      );
    });
  }
}

if (!customElements.get('mix-match-discount-popup')) {
  customElements.define('mix-match-discount-popup', MixMatchDiscountPopup);
}
